import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, ArrowLeft, Download, Info, Search, X } from 'lucide-react';
import { ChatHeader } from '../components/ChatHeader';
import { Composer, type ComposerHandle, type QuotedMessageInfo } from '../components/Composer';
import { WindowBanner } from '../components/WindowBanner';
import { OutsideWindowPicker } from '../components/OutsideWindowPicker';
import { CustomAttrPanel } from '../components/CustomAttrPanel';
import { MessageThread, type MessageThreadHandle } from '../components/MessageThread';
import { ConversationHistoryStack } from '../components/ConversationHistoryStack';
import { SubscriberPanel } from '../components/SubscriberPanel';
import { useAuth } from '../hooks/useAuth';
import { useMessages } from '../hooks/useMessages';
import { api, dispatchCodeHint, dispatchFailed, downloadAuthed, type DispatchSummary } from '../lib/api';
import { db } from '../lib/firebase';
import { useUiMode } from '../lib/uiMode';
import type { Conversation } from '../lib/types';

export function ChatDetailPage() {
  const { chatId } = useParams<{ chatId: string }>();
  // Simple view drops the transcript-export buttons and the custom-attributes
  // strip; the advanced view is unchanged. The right-hand customer panel makes
  // its own call there too — in simple view it opens as a summary chip and the
  // rest of it is one click away (see SubscriberPanel).
  const { advanced } = useUiMode();
  const [searchParams] = useSearchParams();
  const custAction = searchParams.get('cust') as
    | 'panel'
    | 'verify'
    | 'ticket'
    | 'lines'
    | 'notes'
    | 'block'
    | null;
  const { user, claims } = useAuth();
  const [chat, setChat] = useState<Conversation | null>(null);
  const { messages } = useMessages(chatId ?? null);
  const [actionTick, setActionTick] = useState(0);
  const [quotedMessage, setQuotedMessage] = useState<QuotedMessageInfo | null>(null);
  const composerRef = useRef<ComposerHandle | null>(null);
  const threadRef = useRef<MessageThreadHandle | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Keep the thread pinned to the latest message. The stacked conversation
  // history loads async and inserts height ABOVE the messages, so we re-pin
  // when messages arrive, the chat changes, or the history finishes loading.
  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);
  // The last send that did NOT reach the customer. The reply endpoint answers
  // with the dispatch outcome (contract A) and this is where it stops being
  // thrown away — the bubble carries the same truth, but the agent's eyes are on
  // the composer they just typed into.
  const [dispatchNotice, setDispatchNotice] = useState<DispatchSummary | null>(null);
  const [outsideWindowSel, setOutsideWindowSel] = useState<
    | { kind: 'template'; template: { name: string; language: string } }
    | { kind: 'tag'; tag: 'CONFIRMED_EVENT_UPDATE' | 'POST_PURCHASE_UPDATE' | 'ACCOUNT_UPDATE' | 'HUMAN_AGENT' }
    | null
  >(null);

  useEffect(() => {
    if (!chatId) return;
    const unsub = onSnapshot(doc(db, 'chats', chatId), (snap) => {
      if (snap.exists()) setChat({ id: snap.id, ...(snap.data() as Omit<Conversation, 'id'>) });
    });
    return unsub;
  }, [chatId]);

  // A reply-quote belongs to ONE conversation. Clear it when switching chats so
  // a quote set in chat A doesn't leak into chat B's composer (looked like the
  // system was "suggesting" the other chat's message).
  useEffect(() => {
    setQuotedMessage(null);
    setDispatchNotice(null);
  }, [chatId]);

  useEffect(() => {
    if (!chatId || !user || !claims?.tenantCode) return;
    if (messages.length === 0) return;
    const t = setTimeout(() => {
      api.markRead(claims.tenantCode!, user.uid, chatId).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [chatId, user?.uid, claims?.tenantCode, messages.length]);

  // Pin to bottom on chat switch / new messages / action refresh.
  useEffect(() => {
    scrollToBottom();
  }, [chatId, messages.length, actionTick]);

  // On a customer deep-link from the inbox menu, reveal the panel on mobile
  // (it's always visible on desktop). SubscriberPanel opens the matching modal.
  useEffect(() => {
    if (custAction && window.matchMedia('(max-width: 767px)').matches) setShowInfoDrawer(true);
  }, [custAction]);

  if (!chatId) return null;
  if (!chat || !user || !claims?.tenantCode) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 text-sm">
        Loading conversation…
      </div>
    );
  }

  const tenantCode = claims.tenantCode;

  async function reply(
    content: string,
    internal: boolean,
    attachment?: { path: string; mimeType: string; name?: string },
    quoted?: { quotedMessageId: string; quotedPreview: string },
    extras?: { subject?: string; usedDraft?: string },
  ) {
    if (!chatId) return;
    setDispatchNotice(null);
    const res = await api.reply({
      tenantCode,
      chatId,
      agentUid: user!.uid,
      content,
      internal,
      ...(attachment
        ? {
            attachmentPath: attachment.path,
            attachmentMimeType: attachment.mimeType,
            attachmentName: attachment.name,
          }
        : {}),
      ...(quoted
        ? { quotedMessageId: quoted.quotedMessageId, quotedPreview: quoted.quotedPreview }
        : {}),
      ...(extras?.subject ? { subject: extras.subject } : {}),
      ...(extras?.usedDraft ? { usedDraft: extras.usedDraft } : {}),
      ...(outsideWindowSel?.kind === 'template' ? { template: outsideWindowSel.template } : {}),
      ...(outsideWindowSel?.kind === 'tag' ? { messengerTag: outsideWindowSel.tag } : {}),
    });
    // The message IS in the chat either way — `dispatch` says whether it left
    // the building. Deliberately not thrown: a throw makes the composer restore
    // the draft, and the agent sends the same text again into the same dead
    // channel. Show it instead, loudly, and leave the message on screen with its
    // own failed receipt.
    if (dispatchFailed(res.dispatch)) {
      setDispatchNotice(res.dispatch ?? null);
      // Keep the template/tag selection: it is very likely what has to change,
      // and clearing it would make the retry silently in-window.
      return;
    }
    // Reset after a successful send so the picker doesn't silently re-use
    // the previous template on the next message.
    setOutsideWindowSel(null);
  }

  async function downloadTranscript(format: 'text' | 'html') {
    if (!chatId) return;
    const ext = format === 'html' ? 'html' : 'txt';
    const path = `/agent/transcript?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}&format=${format}`;
    try {
      await downloadAuthed(path, `chat-${chatId}.${ext}`);
    } catch (err) {
      alert(`Download failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  const rawChat = chat as unknown as {
    suggestedReply?: { content: string; confidence?: number };
    suggestedReplies?: Array<{ tone?: string; content: string; confidence?: number }>;
  };
  const suggestedReply = rawChat.suggestedReply ?? null;
  const suggestedReplies = Array.isArray(rawChat.suggestedReplies) && rawChat.suggestedReplies.length > 0
    ? rawChat.suggestedReplies
    : null;

  return (
    <div className="flex h-full overflow-hidden relative">
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <ChatHeader
          chat={chat}
          tenantCode={tenantCode}
          agentUid={user.uid}
          onAction={() => setActionTick((t) => t + 1)}
        />
        <div className="px-3 py-1 bg-white border-b border-gray-100 flex gap-2 text-[11px] items-center flex-shrink-0">
          <Link
            to="/inbox"
            className="md:hidden inline-flex items-center gap-1 px-2 py-0.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
            title="Back to inbox"
          >
            <ArrowLeft className="w-3 h-3" /> Inbox
          </Link>
          {advanced && (
            <>
              <button
                onClick={() => downloadTranscript('text')}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
                title="Download as plain text"
              >
                <Download className="w-3 h-3" /> .txt
              </button>
              <button
                onClick={() => downloadTranscript('html')}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
                title="Download as HTML"
              >
                <Download className="w-3 h-3" /> .html
              </button>
            </>
          )}
          <button
            onClick={() => threadRef.current?.openSearch()}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
            title="Search in this conversation (⌘/Ctrl+F)"
          >
            <Search className="w-3 h-3" /> Search
          </button>
          {chat.contactId && (
            <button
              onClick={() => setShowInfoDrawer(true)}
              className="md:hidden ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
              title="Customer info"
            >
              <Info className="w-3 h-3" /> Info
            </button>
          )}
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-gray-50" key={actionTick}>
          {chat.contactId && (
            <ConversationHistoryStack
              contactId={chat.contactId}
              contactEmail={chat.contactEmail}
              contactPhone={chat.contactPhone}
              currentChatId={chatId}
              onLoaded={scrollToBottom}
            />
          )}
          <MessageThread
            ref={threadRef}
            messages={messages}
            tenantCode={tenantCode}
            chatId={chatId}
            agentUid={user.uid}
            channel={chat.channel}
            onQuote={(m) => setQuotedMessage({ id: m.id, content: m.content, type: m.type })}
          />
        </div>
        {advanced && (
          <CustomAttrPanel
            tenantCode={tenantCode}
            agentUid={user.uid}
            chatId={chatId}
            values={(chat as unknown as { customAttrs?: Record<string, unknown> }).customAttrs ?? {}}
          />
        )}
        <WindowBanner
          channel={chat.channel}
          lastInboundAt={(chat as unknown as { lastInboundAt?: number }).lastInboundAt}
        />
        {(() => {
          const li = (chat as unknown as { lastInboundAt?: number }).lastInboundAt;
          const expired = !li || Date.now() - li >= 24 * 60 * 60 * 1000;
          if (!expired) return null;
          if (chat.channel !== 'whatsapp' && chat.channel !== 'facebook') return null;
          return (
            <OutsideWindowPicker
              tenantCode={tenantCode}
              inboxId={chat.inboxId}
              channel={chat.channel as 'whatsapp' | 'facebook'}
              selected={outsideWindowSel}
              onSelect={setOutsideWindowSel}
            />
          );
        })()}
        {dispatchNotice && (
          <div
            role="alert"
            dir="auto"
            className="flex items-start gap-2 px-3 py-2 border-t border-rose-300 bg-rose-50 text-rose-900 text-xs"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">
                {dispatchNotice.status === 'denied'
                  ? 'Blocked before it was sent — the customer did not get your reply'
                  : 'Not delivered — the customer did not get your reply'}
              </div>
              {dispatchNotice.reason && <p className="mt-0.5 break-words">{dispatchNotice.reason}</p>}
              {dispatchCodeHint(dispatchNotice.code) && (
                <p className="mt-0.5 font-medium">{dispatchCodeHint(dispatchNotice.code)}</p>
              )}
              <p className="mt-0.5 opacity-80">
                It is still in the conversation above, marked as not delivered — resend it from there
                once the cause is fixed.
              </p>
            </div>
            <button
              onClick={() => setDispatchNotice(null)}
              className="shrink-0 text-rose-700 hover:text-rose-900"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <Composer
          ref={composerRef}
          tenantCode={tenantCode}
          chatId={chatId}
          agentUid={user.uid}
          onSend={reply}
          disabled={chat.status === 'resolved'}
          suggestedReply={suggestedReply}
          suggestedReplies={suggestedReplies}
          quotedMessage={quotedMessage}
          onClearQuoted={() => setQuotedMessage(null)}
          channel={chat.channel}
          defaultSubject={(() => {
            const ext = (chat as unknown as { externalIds?: Record<string, unknown> }).externalIds ?? {};
            const last = ext.lastSentEmailSubject as string | undefined;
            const inbound = ext.emailSubject as string | undefined;
            if (last && last.length > 0) return last;
            if (inbound && inbound.length > 0) return inbound.startsWith('Re:') ? inbound : `Re: ${inbound}`;
            return '';
          })()}
        />
      </div>
      {chat.contactId && (
        <>
          <div className="hidden md:block h-full">
            <SubscriberPanel
              tenantCode={tenantCode}
              contactId={chat.contactId}
              currentChatId={chatId}
              agentUid={user?.uid}
              initialAction={custAction ?? undefined}
            />
          </div>
          {showInfoDrawer && (
            <div className="md:hidden fixed inset-0 z-40">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setShowInfoDrawer(false)}
              />
              <div className="absolute inset-y-0 right-0 w-80 max-w-[88vw] bg-white shadow-xl flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-white">
                  <span className="text-sm font-semibold text-gray-900">Customer info</span>
                  <button
                    onClick={() => setShowInfoDrawer(false)}
                    className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {/*
                    This drawer only exists because the agent tapped "Info" —
                    that tap IS the click that opens the simple view's summary
                    chip, so don't hand them a chip to tap again.
                  */}
                  <SubscriberPanel
                    tenantCode={tenantCode}
                    contactId={chat.contactId}
                    currentChatId={chatId}
                    agentUid={user?.uid}
                    initialAction={custAction ?? undefined}
                    startExpanded
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
